/**
 * Phone numbers without a country code: which country a lead is in, and the "+<code>" that country's numbers start with.
 * Shared by the server (when leads become campaign contacts) and the Leads page (to show what will happen first).
 *
 * Deliberately small: a calling-code table and a few rules, not a phone-number validator. A number it cannot place with
 * confidence is returned exactly as it came in.
 */

/** ISO 3166 region -> calling code. Countries of the North American plan all share 1. */
export const DIAL_CODES: Record<string, string> = {
  AF: "93", AL: "355", DZ: "213", AD: "376", AO: "244", AR: "54", AM: "374", AU: "61", AT: "43", AZ: "994", BS: "1", BH: "973",
  BD: "880", BB: "1", BY: "375", BE: "32", BZ: "501", BJ: "229", BT: "975", BO: "591", BA: "387", BW: "267", BR: "55", BN: "673",
  BG: "359", BF: "226", BI: "257", KH: "855", CM: "237", CA: "1", CV: "238", CF: "236", TD: "235", CL: "56", CN: "86", CO: "57",
  KM: "269", CG: "242", CD: "243", CR: "506", CI: "225", HR: "385", CU: "53", CY: "357", CZ: "420", DK: "45", DJ: "253", DO: "1",
  EC: "593", EG: "20", SV: "503", GQ: "240", ER: "291", EE: "372", SZ: "268", ET: "251", FJ: "679", FI: "358", FR: "33", GA: "241",
  GM: "220", GE: "995", DE: "49", GH: "233", GR: "30", GT: "502", GN: "224", GW: "245", GY: "592", HT: "509", HN: "504", HK: "852",
  HU: "36", IS: "354", IN: "91", ID: "62", IR: "98", IQ: "964", IE: "353", IL: "972", IT: "39", JM: "1", JP: "81", JO: "962",
  KZ: "7", KE: "254", KW: "965", KG: "996", LA: "856", LV: "371", LB: "961", LS: "266", LR: "231", LY: "218", LI: "423", LT: "370",
  LU: "352", MO: "853", MG: "261", MW: "265", MY: "60", MV: "960", ML: "223", MT: "356", MR: "222", MU: "230", MX: "52", MD: "373",
  MC: "377", MN: "976", ME: "382", MA: "212", MZ: "258", MM: "95", NA: "264", NP: "977", NL: "31", NZ: "64", NI: "505", NE: "227",
  NG: "234", MK: "389", NO: "47", OM: "968", PK: "92", PS: "970", PA: "507", PG: "675", PY: "595", PE: "51", PH: "63", PL: "48",
  PT: "351", PR: "1", QA: "974", RO: "40", RU: "7", RW: "250", SA: "966", SN: "221", RS: "381", SC: "248", SL: "232", SG: "65",
  SK: "421", SI: "386", SO: "252", ZA: "27", KR: "82", SS: "211", ES: "34", LK: "94", SD: "249", SR: "597", SE: "46", CH: "41",
  SY: "963", TW: "886", TJ: "992", TZ: "255", TH: "66", TL: "670", TG: "228", TT: "1", TN: "216", TR: "90", TM: "993", UG: "256",
  UA: "380", AE: "971", GB: "44", US: "1", UY: "598", UZ: "998", VE: "58", VN: "84", YE: "967", ZM: "260", ZW: "263",
};

/** Names a location is commonly written with that Intl's English name for the region does not match. */
const COUNTRY_ALIASES: Record<string, string> = {
  usa: "US", "u.s.": "US", "u.s.a.": "US", america: "US", "united states of america": "US",
  uk: "GB", "u.k.": "GB", england: "GB", scotland: "GB", wales: "GB", "northern ireland": "GB", "great britain": "GB", britain: "GB",
  uae: "AE", "u.a.e.": "AE", emirates: "AE", "south korea": "KR", korea: "KR", "republic of korea": "KR",
  russia: "RU", "russian federation": "RU", vietnam: "VN", "viet nam": "VN", turkey: "TR", "türkiye": "TR", turkiye: "TR",
  czechia: "CZ", "czech republic": "CZ", "ivory coast": "CI", "hong kong sar": "HK", "hong kong sar china": "HK", macau: "MO",
  holland: "NL", "the netherlands": "NL", burma: "MM", swaziland: "SZ", eswatini: "SZ", "north macedonia": "MK", macedonia: "MK",
  philippines: "PH", "the philippines": "PH", "people's republic of china": "CN", prc: "CN", "taiwan, china": "TW",
};

let nameToIso: Map<string, string> | null = null;

function namesIndex(): Map<string, string> {
  if (nameToIso) return nameToIso;
  const map = new Map<string, string>();
  try {
    const names = new Intl.DisplayNames(["en"], { type: "region" });
    for (const iso of Object.keys(DIAL_CODES)) {
      const n = names.of(iso);
      if (n && n !== iso) map.set(n.toLowerCase(), iso);
    }
  } catch { /* an engine without region names still has the aliases */ }
  for (const [alias, iso] of Object.entries(COUNTRY_ALIASES)) map.set(alias, iso);
  nameToIso = map;
  return map;
}

/** English name of a region in DIAL_CODES, for pickers. Falls back to the code. */
export function countryName(iso: string): string {
  try { return new Intl.DisplayNames(["en"], { type: "region" }).of(iso) || iso; } catch { return iso; }
}

/** The region a location like "Pune, Maharashtra, India" is in, judged by its last segment. Null when it names none. */
export function countryFromLocation(location: string | null | undefined): string | null {
  const parts = String(location ?? "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (parts.length === 0) return null;
  const index = namesIndex();
  // The country is written last; a one-word location may itself be a country ("India").
  for (let i = parts.length - 1; i >= Math.max(0, parts.length - 2); i--) {
    const hit = index.get(parts[i]);
    if (hit) return hit;
  }
  return null;
}

/** A region code from a browser locale such as "en-IN", or null. Only regions in DIAL_CODES count. */
export function regionFromLocale(locale: string | null | undefined): string | null {
  const m = /[-_]([A-Za-z]{2})\b/.exec(String(locale ?? ""));
  const iso = m?.[1].toUpperCase();
  return iso && DIAL_CODES[iso] ? iso : null;
}

const MIN_DIGITS = 8;
const MAX_DIGITS = 15;

/**
 * "+<code><number>" for a number written without its country code, using the region given. A number that already starts
 * with "+" is returned as it is. So is one that cannot be placed with confidence: no region, too short or too long.
 *
 *   9876543210   + IN -> +919876543210      (a national number)
 *   09876543210  + IN -> +919876543210      (the leading 0 is the trunk prefix, not part of the number)
 *   919876543210 + IN -> +919876543210      (the code was there, only the "+" was missing)
 *   0044 7911 123456 -> +447911123456       (00 is the international prefix, whatever the region)
 *
 * Limit: a short national number that begins with its own country's code (a Singapore 8-digit number starting 65) written
 * with the code but no "+" reads as a national number and gets the code twice. Numbers like that should carry the "+".
 */
export function withCountryCode(phone: string | null | undefined, region: string | null | undefined): string {
  const raw = String(phone ?? "").trim();
  if (!raw || raw.startsWith("+")) return raw;
  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw;

  if (digits.startsWith("00")) {
    const intl = digits.slice(2);
    return intl.length >= MIN_DIGITS && intl.length <= MAX_DIGITS ? `+${intl}` : raw;
  }

  const code = region ? DIAL_CODES[region.toUpperCase()] : undefined;
  if (!code) return raw;

  // The code is already there, the "+" is what is missing. Needs a national part long enough that it cannot be a national
  // number which merely starts with the same digits (a 10-digit Indian number beginning 91).
  const nationalIfCoded = digits.length - code.length;
  if (digits.startsWith(code) && nationalIfCoded >= (code === "1" ? 10 : 9) && digits.length <= MAX_DIGITS) return `+${digits}`;

  const national = digits.replace(/^0+/, "");
  const total = code.length + national.length;
  if (national.length < 6 || total < MIN_DIGITS || total > MAX_DIGITS) return raw;
  return `+${code}${national}`;
}

/** The main timezone of a country, for the calling hours of a campaign aimed at it. Countries that span several are left out unless one dominates. */
export const COUNTRY_TIMEZONES: Record<string, string> = {
  AE: "Asia/Dubai", SA: "Asia/Riyadh", QA: "Asia/Qatar", KW: "Asia/Kuwait", BH: "Asia/Bahrain", OM: "Asia/Muscat", IL: "Asia/Jerusalem",
  TR: "Europe/Istanbul", EG: "Africa/Cairo", ZA: "Africa/Johannesburg", NG: "Africa/Lagos", KE: "Africa/Nairobi",
  IN: "Asia/Kolkata", PK: "Asia/Karachi", BD: "Asia/Dhaka", LK: "Asia/Colombo", NP: "Asia/Kathmandu",
  SG: "Asia/Singapore", MY: "Asia/Kuala_Lumpur", ID: "Asia/Jakarta", PH: "Asia/Manila", TH: "Asia/Bangkok", VN: "Asia/Ho_Chi_Minh",
  HK: "Asia/Hong_Kong", CN: "Asia/Shanghai", JP: "Asia/Tokyo", KR: "Asia/Seoul", AU: "Australia/Sydney", NZ: "Pacific/Auckland",
  GB: "Europe/London", IE: "Europe/Dublin", DE: "Europe/Berlin", FR: "Europe/Paris", ES: "Europe/Madrid", IT: "Europe/Rome",
  NL: "Europe/Amsterdam", CH: "Europe/Zurich", SE: "Europe/Stockholm", PL: "Europe/Warsaw", PT: "Europe/Lisbon",
  US: "America/New_York", CA: "America/Toronto", MX: "America/Mexico_City", BR: "America/Sao_Paulo",
};

export function timezoneForCountry(region: string | null | undefined): string | null {
  return (region && COUNTRY_TIMEZONES[region.toUpperCase()]) || null;
}
